// src/pages/VitCompilati.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { generateVitPdfBlob } from '../pdf/clientPdf';
import { Card } from '../components/ui';

const API_BASE = '/api';

const templates = [
  { id: 'CIESSE_IMPIANTI', label: 'Ciesse Impianti', file: '/VIT/CiesseImpianti.pdf' },
  { id: 'CIESSE_SERVICE', label: 'Ciesse Service', file: '/VIT/CiesseService.pdf' },
];

const formatEuro = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  const normalized = String(val).replace(',', '.');
  const num = parseFloat(normalized);
  if (!Number.isFinite(num)) return '';
  return num.toFixed(2).replace('.', ',');
};

function getCleanUserName(user) {
  if (!user) return '';
  const candidates = [];
  if (user.username) candidates.push(user.username);
  if (user.nome || user.cognome) {
    candidates.push(`${user.nome || ''} ${user.cognome || ''}`.trim());
  }
  if (user.fullName) candidates.push(user.fullName);
  let name = candidates.find(Boolean) || '';
  name = name
    .split(/\s+/)
    .filter((part) => !part.includes('@'))
    .join(' ')
    .trim();
  if (name) return name;
  if (user.email && user.email.includes('@')) {
    const local = user.email.split('@')[0];
    return local.replace('.', ' ').trim();
  }
  return '';
}

function VitCompilati() {
  const location = useLocation();
  const navigate = useNavigate();

  const fromWip = location.state?.fromWorkInProgress || {};
  const workInProgressId = fromWip._id || null;
  const fromWorkOrder = fromWip.workOrder || {};
  const summary = fromWip.summary || {};

  const initialStatus = fromWorkOrder.status || 'in_corso';
  const [workStatus, setWorkStatus] = useState(initialStatus);

  const baseInternalCode =
    fromWorkOrder.internalCode ||
    fromWorkOrder.codiceInterno ||
    summary.codiceInterno ||
    '';

  const wipIteration =
    typeof fromWip.iteration === 'number'
      ? fromWip.iteration
      : typeof fromWorkOrder.iteration === 'number'
      ? fromWorkOrder.iteration
      : 0;

  const fullInternalCode =
    wipIteration > 0 ? `${baseInternalCode}-${wipIteration}` : baseInternalCode;

  const savedFormData = fromWip.formData || {};
  const savedTecnicoSignature = fromWip.tecnicoSignature || null;
  const savedClienteSignature = fromWip.clienteSignature || null;

  // ----- log note -----
  let initialNoteLog = [];
  const rawLog =
    savedFormData.noteGeneraliLog || fromWip.noteGeneraliLog;

  if (Array.isArray(rawLog)) initialNoteLog = rawLog;

  if (initialNoteLog.length === 0) {
    const initialText =
      fromWorkOrder.noteGenerali ||
      fromWorkOrder.descrizione ||
      fromWorkOrder.description ||
      '';
    if (initialText) {
      initialNoteLog = [
        {
          text: initialText,
          userId: null,
          userName: 'Creazione lavorazione',
          createdAt: fromWorkOrder.createdAt || null,
          iteration:
            typeof fromWorkOrder.iteration === 'number'
              ? fromWorkOrder.iteration
              : 0,
        },
      ];
    }
  }

  const [selectedTemplate, setSelectedTemplate] =
    useState('CIESSE_IMPIANTI');
  const [pdfUrl, setPdfUrl] = useState(
    templates.find((t) => t.id === 'CIESSE_IMPIANTI')?.file || null
  );

  const [currentUserName, setCurrentUserName] = useState('');

  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await authFetch(`${API_BASE}/user`);
        if (!res.ok) return;
        const me = await res.json();
        const displayName = getCleanUserName(me);
        setCurrentUserName(displayName || '');
      } catch (err) {
        console.error('Errore user (VIT COMPILATI):', err);
      }
    };
    loadUser();
  }, []);

  const [formData, setFormData] = useState(() => {
    let fallbackData = '';
    if (savedFormData.data) fallbackData = savedFormData.data;
    else if (summary.data) fallbackData = summary.data;
    else if (fromWorkOrder.dateIntervento || fromWorkOrder.dataIntervento) {
      const d = new Date(
        fromWorkOrder.dateIntervento || fromWorkOrder.dataIntervento
      );
      if (!Number.isNaN(d.getTime()))
        fallbackData = d.toISOString().slice(0, 10);
    }

    const base = {
      data: '',
      cliente: '',
      impianto: '',
      luogo: '',
      telefono: '',
      tecnico: '',
      numeroIntervento: '',
      matricolaImpianto: '',
      inventario: '',
      noteGenerali: '',
      dirittoFissoQuantita: '1',
      dirittoFissoEuro: '',
      oreLavoroQuantita: '',
      oreLavoroEuro: '',
      ricambi: Array.from({ length: 8 }, () => ({
        quantita: '',
        descrizione: '',
        euro: '',
      })),
      lavoriEseguiti: '',
    };

    const merged = { ...base, ...savedFormData };

    return {
      ...merged,
      data: fallbackData || merged.data || '',
      cliente:
        merged.cliente ||
        summary.cliente ||
        fromWorkOrder.cliente ||
        fromWorkOrder.companyName ||
        '',
      impianto:
        merged.impianto ||
        fromWorkOrder.impianto ||
        fromWorkOrder.presidio ||
        fromWorkOrder.equipmentPresidio ||
        '',
      luogo:
        merged.luogo ||
        fromWorkOrder.luogoIntervento ||
        fromWorkOrder.luogo ||
        fromWorkOrder.indirizzo ||
        fromWorkOrder.place ||
        '',
      telefono:
        merged.telefono ||
        fromWorkOrder.telefono ||
        fromWorkOrder.customerPhone ||
        '',
      numeroIntervento:
        merged.numeroIntervento ||
        fromWorkOrder.riferimentoChiamata ||
        fromWorkOrder.codiceEsterno ||
        fromWorkOrder.externalCode ||
        '',
      matricolaImpianto:
        merged.matricolaImpianto ||
        fromWorkOrder.matricola ||
        fromWorkOrder.seriale ||
        fromWorkOrder.equipmentSerial ||
        '',
      inventario:
        merged.inventario ||
        fromWorkOrder.inventario ||
        fromWorkOrder.equipmentInventory ||
        '',
      dirittoFissoQuantita: merged.dirittoFissoQuantita || '1',
      dirittoFissoEuro: merged.dirittoFissoEuro || '',
      oreLavoroQuantita: merged.oreLavoroQuantita || '',
      oreLavoroEuro: merged.oreLavoroEuro || '',
      noteGenerali: '',
    };
  });

  const [noteLog, setNoteLog] = useState(initialNoteLog);

  const [tecnicoSignature, setTecnicoSignature] =
    useState(savedTecnicoSignature);
  const [clienteSignature, setClienteSignature] =
    useState(savedClienteSignature);

  const [signModal, setSignModal] = useState({ open: false, type: null });
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const t = templates.find((tmp) => tmp.id === selectedTemplate);
    setPdfUrl(t ? t.file : null);
  }, [selectedTemplate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRicambioChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.ricambi];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, ricambi: updated };
    });
  };

  // -------- firma --------
  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const moveDraw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const endDraw = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const openSignatureModal = (type) => {
    setError('');
    setSuccessMessage('');
    setSignModal({ open: true, type });
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, 50);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      setSignModal({ open: false, type: null });
      return;
    }
    const dataUrl = canvas.toDataURL('image/png');
    if (signModal.type === 'tecnico') setTecnicoSignature(dataUrl);
    if (signModal.type === 'cliente') setClienteSignature(dataUrl);
    setSignModal({ open: false, type: null });
  };

  const clearSignaturePreview = (type) => {
    if (type === 'tecnico') setTecnicoSignature(null);
    if (type === 'cliente') setClienteSignature(null);
  };


// -------- genera PDF --------
const handleGeneratePdf = async () => {
  setError('');
  setSuccessMessage('');

  if (!formData.data || !formData.cliente) {
    setError('Compila almeno data e cliente prima di generare il PDF.');
    return;
  }

  if (workStatus !== 'sospesa' && workStatus !== 'chiusa') {
    setError(
      'Seleziona prima "Metti in sospeso" o "Chiudi lavorazione" prima di generare il VIT.'
    );
    return;
  }

  setLoading(true);

  try {
    const newNoteText = (formData.noteGenerali || '').trim();
    let updatedLog = noteLog;

    if (newNoteText) {
      const entry = {
        text: newNoteText,
        userId: null,
        userName: currentUserName || 'Tecnico',
        createdAt: new Date().toISOString(),
        iteration: wipIteration,
      };
      updatedLog = [...noteLog, entry];
    }

    const pdfFormData = {
      ...formData,
      noteGenerali: '',
      codiceInterno: fullInternalCode || '',
    };

    const blob = await generateVitPdfBlob({
      template: selectedTemplate,
      formData: pdfFormData,
      tecnicoSignature,
      clienteSignature,
      inviaReport: true,
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verbale_intervento_tecnico_${fullInternalCode || 'SENZA-CODICE'}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setSuccessMessage('VIT generato correttamente.');

    if (workInProgressId) {
      const newSummary = {
        ...summary,
        codiceInterno: fullInternalCode,
        codiceEsterno:
          summary.codiceEsterno ||
          formData.numeroIntervento ||
          fromWorkOrder.codiceEsterno ||
          '',
        data: formData.data,
        cliente: formData.cliente,
        impianto: formData.impianto,
        luogo: formData.luogo,
        telefono: formData.telefono,
        modello:
          summary.modello ||
          fromWorkOrder.modello ||
          fromWorkOrder.equipmentModel ||
          '',
        matricola:
          summary.matricola ||
          formData.matricolaImpianto ||
          fromWorkOrder.seriale ||
          fromWorkOrder.equipmentSerial ||
          '',
        inventario:
          summary.inventario ||
          formData.inventario ||
          fromWorkOrder.inventario ||
          fromWorkOrder.equipmentInventory ||
          '',
      };

      const formDataToSave = {
        ...formData,
        noteGenerali: '',
        noteGeneraliLog: updatedLog,
      };

      try {
        const resp = await authFetch(
          `${API_BASE}/work/in-progress/${workInProgressId}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              summary: newSummary,
              formData: formDataToSave,
              tecnicoSignature,
              clienteSignature,
              status: workStatus,
              notesAppend: newNoteText,
            }),
          }
        );

        if (resp.ok) {
          const updatedWip = await resp.json();
          const serverLog =
            updatedWip.formData?.noteGeneraliLog || updatedLog;

          setNoteLog(serverLog);
          setFormData((prev) => ({ ...prev, noteGenerali: '' }));
        } else {
          // fallback: almeno aggiorno il log locale
          setNoteLog(updatedLog);
          setFormData((prev) => ({ ...prev, noteGenerali: '' }));
        }
      } catch (err2) {
        console.error(
          'Errore aggiornamento WorkInProgress (VIT COMPILATI):',
          err2
        );
        // anche in errore, tengo il log locale aggiornato
        setNoteLog(updatedLog);
        setFormData((prev) => ({ ...prev, noteGenerali: '' }));
      }
    }
  } catch (err) {
    console.error('Errore generazione VIT (compilato):', err);
    setError(err.message || 'Errore inatteso durante la generazione');
  } finally {
    setLoading(false);
  }
};


  // -------- render --------
  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-8">
        {/* header */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
              Verbale di Intervento Tecnico (report compilato)
            </h1>
            <p className="text-sm text-gray-600">
              Verifica e correggi il VIT già compilato: costi, ricambi, note e
              firme.
            </p>
          </div>
          <button
            onClick={() => navigate('/lavorazioni/in-corso')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Indietro
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-6">
          I dati sono ripescati dalla lavorazione in corso. Le Note generali
          vengono salvate come storico interno e non finiscono sul PDF.
        </p>

        {/* scelta template */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedTemplate(t.id)}
              className={`w-full text-left p-4 rounded-xl border transition shadow-sm ${
                selectedTemplate === t.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-blue-400'
              }`}
            >
              <h2 className="font-semibold text-gray-800">{t.label}</h2>
              <p className="text-xs text-gray-500 mt-1">
                Modello PDF: {t.file.replace('/VIT/', '')}
              </p>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[2.1fr,1.4fr] gap-6">
          {/* SX */}
          <div className="space-y-4">
            {/* dati intervento */}
            <Card className="shadow-sm p-4 space-y-3 text-sm">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Codice interno
                </label>
                <input
                  type="text"
                  value={fullInternalCode}
                  readOnly
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700"
                />
              </div>

              {[
                { name: 'data', label: 'Data intervento', type: 'date' },
                { name: 'cliente', label: 'Cliente' },
                { name: 'impianto', label: 'Istituto' },
                { name: 'luogo', label: 'Indirizzo' },
                { name: 'telefono', label: 'Telefono' },
                { name: 'numeroIntervento', label: 'Riferimento chiamata' },
                { name: 'modello', label: 'Modello' },
                { name: 'matricolaImpianto', label: 'Matricola' },
                { name: 'inventario', label: 'Inventario' },
              ].map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label}
                  </label>
                  <input
                    type={field.type || 'text'}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    className={
                      'mt-1 w-full max-w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ' +
                      (field.type === 'date' ? 'appearance-none' : '')
                    }
                  />
                </div>
              ))}

              {/* note generali */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Note generali (uso interno, NON sul PDF)
                </label>

                {noteLog.length > 0 && (
                  <div className="mt-1 mb-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 px-3 py-2 text-xs space-y-1">
                    {noteLog.map((n, idx) => (
                      <div
                        key={idx}
                        className="border-b border-gray-200 last:border-0 pb-1 last:pb-0"
                      >
                        <p className="font-semibold text-gray-700">
                          {n.userName || 'Tecnico'}{' '}
                          {n.createdAt
                            ? `(${new Date(n.createdAt).toLocaleString('it-IT')})`
                            : ''}
                        </p>
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {n.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                <textarea
                  name="noteGenerali"
                  value={formData.noteGenerali}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Aggiungi una nota per questo giro. Verrà salvata nello storico ma NON stampata sul PDF."
                />
              </div>
            </Card>

            {/* costi */}
            <Card className="shadow-sm p-4 space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Costi intervento
              </h3>

              <div className="space-y-1 text-sm">
                <p className="font-medium text-gray-700">
                  Diritto fisso chiamata
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    maxLength={2}
                    value={formData.dirittoFissoQuantita || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dirittoFissoQuantita: e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 2),
                      }))
                    }
                    className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1"
                  />
                  <span className="text-xs text-gray-500">nr</span>
                  <input
                    type="text"
                    value={formData.dirittoFissoEuro || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dirittoFissoEuro: e.target.value,
                      }))
                    }
                    onBlur={() =>
                      setFormData((prev) => ({
                        ...prev,
                        dirittoFissoEuro: formatEuro(
                          prev.dirittoFissoEuro
                        ),
                      }))
                    }
                    className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0,00"
                  />
                  <span className="text-xs text-gray-500">Euro</span>
                </div>
              </div>

              <div className="space-y-1 text-sm">
                <p className="font-medium text-gray-700">
                  Ore di lavoro in sito
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    maxLength={2}
                    value={formData.oreLavoroQuantita || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        oreLavoroQuantita: e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 2),
                      }))
                    }
                    className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                  <span className="text-xs text-gray-500">h</span>
                  <input
                    type="text"
                    value={formData.oreLavoroEuro || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        oreLavoroEuro: e.target.value,
                      }))
                    }
                    onBlur={() =>
                      setFormData((prev) => ({
                        ...prev,
                        oreLavoroEuro: formatEuro(prev.oreLavoroEuro),
                      }))
                    }
                    className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0,00"
                  />
                  <span className="text-xs text-gray-500">Euro</span>
                </div>
              </div>
            </Card>

            {/* ricambi */}
            <Card className="shadow-sm p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Pezzi di ricambio
              </h3>
              <p className="text-xs text-gray-500 mb-2">
                Qtà solo numerica (max 3 cifre).
              </p>

              <div className="overflow-x-auto">
                <div className="min-w-[320px] space-y-2 text-xs md:text-sm">
                  <div className="grid grid-cols-[4rem,minmax(0,1fr),5.5rem] gap-2 font-medium text-gray-700">
                    <span className="text-center">Qtà</span>
                    <span className="text-center">Descrizione pezzo</span>
                    <span className="text-right">Euro</span>
                  </div>

                  {formData.ricambi.map((riga, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[4rem,minmax(0,1fr),5.5rem] gap-2"
                    >
                      <input
                        type="text"
                        value={riga.quantita}
                        maxLength={3}
                        onChange={(e) =>
                          handleRicambioChange(
                            index,
                            'quantita',
                            e.target.value.replace(/\D/g, '').slice(0, 3)
                          )
                        }
                        className="border border-gray-300 rounded-lg px-2 py-1 text-xs md:text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="1"
                      />

                      <input
                        type="text"
                        value={riga.descrizione}
                        onChange={(e) =>
                          handleRicambioChange(
                            index,
                            'descrizione',
                            e.target.value
                          )
                        }
                        className="border border-gray-300 rounded-lg px-2 py-1 text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Descrizione pezzo"
                      />

                      <input
                        type="text"
                        value={riga.euro}
                        onChange={(e) =>
                          handleRicambioChange(index, 'euro', e.target.value)
                        }
                        onBlur={() =>
                          handleRicambioChange(
                            index,
                            'euro',
                            formatEuro(riga.euro)
                          )
                        }
                        className="border border-gray-300 rounded-lg px-2 py-1 text-xs md:text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0,00"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* lavori eseguiti + firme + stato + bottone */}
            <Card className="shadow-sm p-4 space-y-4">
              {/* lavori eseguiti (non toccato) */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Lavori eseguiti
                </label>
                <textarea
                  name="lavoriEseguiti"
                  value={formData.lavoriEseguiti}
                  onChange={handleChange}
                  rows={4}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* firme – layout sistemato */}
              <div className="grid sm:grid-cols-2 gap-4">
                {/* tecnico */}
                <div className="border border-gray-200 rounded-xl p-3 flex flex-col gap-3">
                  <p className="text-xs font-semibold text-gray-700">
                    Firma tecnico
                  </p>
                  <div className="flex-1 min-h-[90px] border border-gray-200 rounded-lg bg-white flex items-center justify-center">
                    {tecnicoSignature ? (
                      <img
                        src={tecnicoSignature}
                        alt="Firma tecnico"
                        className="max-h-20 object-contain"
                      />
                    ) : (
                      <p className="text-[11px] text-gray-500">
                        Nessuna firma tecnico presente.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    {tecnicoSignature && (
                      <button
                        type="button"
                        onClick={() => clearSignaturePreview('tecnico')}
                        className="text-[11px] text-red-600 hover:text-red-800"
                      >
                        Rimuovi firma
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => openSignatureModal('tecnico')}
                      className="ml-auto inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Firma come tecnico
                    </button>
                  </div>
                </div>

                {/* cliente */}
                <div className="border border-gray-200 rounded-xl p-3 flex flex-col gap-3">
                  <p className="text-xs font-semibold text-gray-700">
                    Firma cliente
                  </p>
                  <div className="flex-1 min-h-[90px] border border-gray-200 rounded-lg bg-white flex items-center justify-center">
                    {clienteSignature ? (
                      <img
                        src={clienteSignature}
                        alt="Firma cliente"
                        className="max-h-20 object-contain"
                      />
                    ) : (
                      <p className="text-[11px] text-gray-500">
                        Nessuna firma cliente presente.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    {clienteSignature && (
                      <button
                        type="button"
                        onClick={() => clearSignaturePreview('cliente')}
                        className="text-[11px] text-red-600 hover:text-red-800"
                      >
                        Rimuovi firma
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => openSignatureModal('cliente')}
                      className="ml-auto inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Firma come cliente
                    </button>
                  </div>
                </div>
              </div>

              {/* stato lavorazione (logica invariata, solo usata nel check) */}
              <div className="border border-gray-200 rounded-xl p-3 mb-2 bg-gray-50">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Stato lavorazione
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setWorkStatus((prev) =>
                        prev === 'sospesa' ? 'in_corso' : 'sospesa'
                      )
                    }
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                      workStatus === 'sospesa'
                        ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-yellow-400'
                    }`}
                  >
                    {workStatus === 'sospesa'
                      ? 'In attesa di accettazione'
                      : 'Metti in sospeso'}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setWorkStatus((prev) =>
                        prev === 'chiusa' ? 'in_corso' : 'chiusa'
                      )
                    }
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                      workStatus === 'chiusa'
                        ? 'bg-emerald-100 border-emerald-500 text-emerald-800'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-emerald-500'
                    }`}
                  >
                    {workStatus === 'chiusa'
                      ? 'Lavorazione chiusa'
                      : 'Chiudi lavorazione'}
                  </button>
                </div>

                <p className="mt-2 text-[11px] text-gray-500">
                  Lo stato scelto verrà salvato quando generi il PDF aggiornato.
                </p>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              {successMessage && (
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  {successMessage}
                </p>
              )}

              <button
                type="button"
                onClick={handleGeneratePdf}
                disabled={loading}
                className={`w-full py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm transition ${
                  loading
                    ? 'bg-blue-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Generazione in corso...' : 'Genera PDF aggiornato'}
              </button>
            </Card>
          </div>

          {/* DX: anteprima */}
          <Card className="shadow-sm p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Anteprima modello
            </h3>
            {pdfUrl ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden h-[600px]">
                <iframe
                  title="Anteprima PDF VIT"
                  src={pdfUrl}
                  className="w-full h-full"
                />
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Seleziona un modello per visualizzare l&apos;anteprima.
              </p>
            )}
          </Card>
        </div>
      </div>

      {/* modal firma */}
      {signModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onTouchMove={(e) => e.preventDefault()}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-800">
                Firma {signModal.type === 'tecnico' ? 'tecnico' : 'cliente'}
              </h2>
              <button
                onClick={() => setSignModal({ open: false, type: null })}
                className="text-gray-500 hover:text-gray-800 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Firma con il dito o con il mouse. Al termine premi
              &quot;Salva firma&quot;.
            </p>
            <div className="border border-gray-300 rounded-lg bg-gray-50">
              <canvas
                ref={canvasRef}
                width={400}
                height={200}
                className="w-full h-48"
                onMouseDown={startDraw}
                onMouseMove={moveDraw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={moveDraw}
                onTouchEnd={endDraw}
              />
            </div>
            <div className="mt-3 flex justify-between items-center">
              <button
                type="button"
                onClick={() => {
                  const canvas = canvasRef.current;
                  if (!canvas) return;
                  const ctx = canvas.getContext('2d');
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Cancella
              </button>
              <button
                type="button"
                onClick={saveSignature}
                className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
              >
                Salva firma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VitCompilati;
